// Native EventSource does not support headers, so we need a polyfill
import { createEventSource } from "eventsource-client";
import qs from "query-string";
import addPolyfills from "core/shared/polyfills/index";

export default class StreamSource {
  constructor({
    callbacks,
    headers: authHeaders,
    params,
    url = "/",
    withCredentials = "include",
  }) {
    addPolyfills();
    const query = qs.stringify(params);
    this.url = query ? `${url}?${query}` : url;
    this.withCredentials = withCredentials;
    this.shouldReconnect = true;

    this.callbacks = {
      complete: this.close.bind(this),
      error: (err) => {
        // eslint-disable-next-line no-console
        console.error("Stream Error:", this.url, err);
        this.close();
      },
      ...callbacks,
    };

    this.fetchHandler = async (input, init = {}) => {
      // init.headers can be Headers instance, object, or array
      const headers = new Headers(init.headers);

      if (authHeaders) {
        for (const [key, value] of Object.entries(authHeaders)) {
          headers.set(key, value);
        }
      }

      const fetchOptions = { ...init, credentials: withCredentials, headers };
      const response = await fetch(input, fetchOptions);

      // If we get an error status, prevent reconnection
      if (!response.ok) {
        this.shouldReconnect = false;
        // eslint-disable-next-line no-console
        console.error("Stream fetch error:", response.status, this.url);

        if (this.callbacks.error) {
          this.callbacks.error({ status: response.status, url: this.url });
        }
      }

      return response;
    };

    this.handleMessage = (message) => {
      // The message object has: event, data, id
      const eventType = message.event || "message";

      // On 'complete' event, handle close connection to prevent reconnect loop
      if (eventType === "complete") {
        if (this.callbacks.complete) this.callbacks.complete(message);
        return;
      }

      if (this.callbacks[eventType]) this.callbacks[eventType](message);
    };

    this.handleDisconnect = ({ willReconnect }) => {
      // If we've flagged that we shouldn't reconnect, close the connection
      if (!this.shouldReconnect && willReconnect) {
        this.close();
        return false; // Prevent reconnection
      }
      return willReconnect;
    };

    this.evtSrcOpts = {
      fetch: this.fetchHandler,
      onMessage: this.handleMessage,
      onDisconnect: this.handleDisconnect,
      url: this.url,
    };

    this.evtSrc = createEventSource(this.evtSrcOpts);

    // Connect to start receiving events
    this.evtSrc.connect();
  }

  close() {
    this.shouldReconnect = false; // Ensure no reconnection attempts
    this.callbacks.done?.(this);

    if (this.evtSrc) {
      this.evtSrc.close();
      this.evtSrc = null; // Prevent double-close
    }
  }
}
